import { useEffect, useRef, useState } from "react";
import { API_URL } from "@core/constants";
import { AISearchPage } from "@models";
import {
    AiSettingsResponse,
    ByoAiSettings,
    TopTrendsAiSettings,
    toByoAiSettings,
    toTopTrendsAiSettings,
} from "@apiHooks/aiSearchSettings";

const AI_SEARCH_ENDPOINT = "/Ai/Search";

/**
 * Join the endpoint onto the API base without caring whether the configured
 * base has a trailing slash.
 *
 * The two existing clients disagree: apiConfig adds the separator itself
 * (`${API_URL}/${path}`), so `.env.development` sets no trailing slash, while
 * axios normalises either form. Concatenating raw would break against one
 * convention or the other — and a doubled slash 404s on the backend rather
 * than being tidied away.
 */
export function aiSearchUrl(query: string): string {
    const base = (API_URL ?? "").replace(/\/+$/, "");
    return `${base}${AI_SEARCH_ENDPOINT}?query=${encodeURIComponent(query)}`;
}

/** Frame names on the `/Ai/Search` stream. */
const AiSearchFrame = {
    Page: "page",
    Settings: "settings",
    Answer: "answer",
    Done: "done",
    Error: "error",
} as const;

export type AiSearchStreamState = {
    /** Which page the query routes to; null until the first frame lands. */
    page: AISearchPage | null;
    topTrendsSettings: TopTrendsAiSettings | null;
    byoSettings: ByoAiSettings | null;
    /** The answer so far — appended to as tokens arrive. */
    answer: string;
    isRelatedToConsumerPreferences: boolean;
    isRelatedToReportPro: boolean;
    isSettingsLoading: boolean;
    isAnswerLoading: boolean;
    hasFailed: boolean;
};

export const EMPTY_AI_SEARCH_STATE: AiSearchStreamState = {
    page: null,
    topTrendsSettings: null,
    byoSettings: null,
    answer: "",
    isRelatedToConsumerPreferences: false,
    isRelatedToReportPro: false,
    isSettingsLoading: false,
    isAnswerLoading: false,
    hasFailed: false,
};

type DoneFrame = {
    summary?: {
        summary?: string;
        dataRelatedToConsumerPreferences?: boolean;
        dataRelatedToReportPro?: boolean;
    };
};

/**
 * One AI search over one SSE connection.
 *
 * The backend resolves the query in a single agent run, so routing, filters and
 * answer all arrive on the same stream instead of the three separate calls this
 * replaces. Frames land in order: `page` (route on it), `settings` (filter panel
 * and grid), `answer` tokens, then a terminal `done` or `error`.
 *
 * Deliberately keyed on the query TEXT, not on a route or a query-param name:
 * the caller navigates mid-stream (from the AI-search route to TopTrends or BYO,
 * which moves the query from one param to another) and that must not restart the
 * run. This hook's owner sits above the router so the connection survives it.
 *
 * `EventSource` isn't an option — it can't send credentials cross-origin the way
 * the rest of the app's calls do — so this is `fetch` + `ReadableStream`.
 */
export function useAiSearchStream(query: string): AiSearchStreamState {
    const [state, setState] = useState<AiSearchStreamState>(EMPTY_AI_SEARCH_STATE);
    const activeQueryRef = useRef<string>("");
    const trimmedQuery = query.trim();
    // Has the effect below caught up with the query we were just handed?
    const isPreviousRun = activeQueryRef.current !== trimmedQuery;

    useEffect(() => {
        const trimmed = query.trim();

        if (!trimmed) {
            activeQueryRef.current = "";
            setState(EMPTY_AI_SEARCH_STATE);
            return;
        }

        // Same question, different route — keep the in-flight run.
        if (activeQueryRef.current === trimmed) return;

        activeQueryRef.current = trimmed;
        setState({ ...EMPTY_AI_SEARCH_STATE, isSettingsLoading: true, isAnswerLoading: true });

        const controller = new AbortController();

        const read = async () => {
            try {
                const response = await fetch(
                    aiSearchUrl(trimmed),
                    { credentials: "include", signal: controller.signal }
                );

                if (!response.ok || !response.body) {
                    throw new Error(`AI search failed with status ${response.status}`);
                }

                const reader = response.body.getReader();
                const decoder = new TextDecoder();
                let buffer = "";

                for (;;) {
                    const { value, done } = await reader.read();
                    if (done) break;

                    buffer += decoder.decode(value, { stream: true });

                    // Frames are blank-line delimited and can arrive several per
                    // read or split across reads, so drain whole frames only.
                    let boundary = buffer.indexOf("\n\n");
                    while (boundary >= 0) {
                        const frame = buffer.slice(0, boundary);
                        setState(prev => applyFrame(prev, frame));
                        buffer = buffer.slice(boundary + 2);
                        boundary = buffer.indexOf("\n\n");
                    }
                }

                setState(prev => ({ ...prev, isSettingsLoading: false, isAnswerLoading: false }));
            } catch (error) {
                if (controller.signal.aborted) return;

                setState(prev => ({
                    ...prev,
                    isSettingsLoading: false,
                    isAnswerLoading: false,
                    hasFailed: true,
                }));
            }
        };

        read();

        return () => {
            controller.abort();
        };
    }, [query]);

    // Report the run for the CURRENT query, never the last one's.
    //
    // The reset above lives in an effect, so on the render where `query`
    // changes this hook would otherwise hand back the previous question's
    // state — including its `page`. The owner sits above the router and
    // `AiSearch` reads `page` from a descendant, and React runs child effects
    // BEFORE parent effects, so `AiSearch` navigated on the previous
    // question's page and then unmounted; nothing else routes on `page`, so
    // the real one arrived with no listener and the user was stuck on
    // whichever page the question before had resolved to.
    if (isPreviousRun) {
        return trimmedQuery
            ? { ...EMPTY_AI_SEARCH_STATE, isSettingsLoading: true, isAnswerLoading: true }
            : EMPTY_AI_SEARCH_STATE;
    }

    return state;
}

/**
 * Fold one SSE frame into the state. Pure, so the frame contract can be tested
 * without a stream. An unreadable or unrecognised frame returns the state
 * untouched rather than abandoning the run.
 */
export function applyFrame(state: AiSearchStreamState, frame: string): AiSearchStreamState {
    const dataLine = frame.split("\n").find(line => line.startsWith("data:"));
    if (!dataLine) return state;

    let payload: any;
    try {
        payload = JSON.parse(dataLine.slice("data:".length).trim());
    } catch {
        return state;
    }

    switch (payload?.event) {
        case AiSearchFrame.Page:
            return { ...state, page: payload.page ?? AISearchPage.Undefined };

        case AiSearchFrame.Settings: {
            const settings = (payload.settings ?? null) as AiSettingsResponse | null;
            return {
                ...state,
                topTrendsSettings: toTopTrendsAiSettings(settings),
                byoSettings: toByoAiSettings(settings),
                isSettingsLoading: false,
            };
        }

        case AiSearchFrame.Answer:
            return { ...state, answer: state.answer + (payload.token ?? "") };

        case AiSearchFrame.Done: {
            const summary = (payload as DoneFrame).summary;
            return {
                ...state,
                // The answer streamed token by token; only fall back to the
                // terminal copy if nothing arrived (a refusal, or no data).
                answer: state.answer || summary?.summary || "",
                isRelatedToConsumerPreferences: summary?.dataRelatedToConsumerPreferences ?? false,
                isRelatedToReportPro: summary?.dataRelatedToReportPro ?? false,
                isSettingsLoading: false,
                isAnswerLoading: false,
            };
        }

        case AiSearchFrame.Error:
            return { ...state, isSettingsLoading: false, isAnswerLoading: false, hasFailed: true };

        default:
            return state;
    }
}
